import { ApiService } from './api';
import { ApiResponse } from '../types/auth';
import {
  AdminOrderResponse,
  AdminOrderDetailsResponse,
  OrdersRequest,
  AcceptOrderRequest,
  CancelOrderRequest,
  ChangeOrderRestaurantBranchRequest,
  DetachOrderDriverRequest,
  SendOrderToDriverRequest,
  SendMessageToClientRequest,
  UpdateOrderDeliveryPriceRequest,
  DriverPathResponse,
  CreateOrderRequest,
  OrderCreatedResponse,
  RestaurantSearchSuggestion,
  RestaurantFoodResponse,
  GetPreOrderPriceDetailsRequest,
  PreOrderPriceDetailsResponse,
  ProblematicCompleteOrderRequest,
  OrderMetadata
} from '../types/orders';

export class OrdersService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getOrders(request: OrdersRequest): Promise<ApiResponse<AdminOrderResponse[]>> {
    try {
      const params = new URLSearchParams({
        searchQuery: request.searchQuery,
        filter: request.filter,
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });

      const response = await this.apiService.get<AdminOrderResponse[]>(
        `/admin/orders?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch orders',
        success: false
      };
    }
  }

  async getOrderDetails(orderId: number): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.get<AdminOrderDetailsResponse>(
        `/admin/orders/${orderId}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch order details',
        success: false
      };
    }
  }

  async acceptOrder(request: AcceptOrderRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/accept-order',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to accept order',
        success: false
      };
    }
  }

  async cancelOrder(request: CancelOrderRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/cancel-order',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to cancel order',
        success: false
      };
    }
  }

  async changeBranch(request: ChangeOrderRestaurantBranchRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/change-order-restaurant-branch',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to change branch',
        success: false
      };
    }
  }

  async detachDriver(request: DetachOrderDriverRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/detach-driver',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to detach driver',
        success: false
      };
    }
  }

  async sendOrderToDriver(request: SendOrderToDriverRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/sent-to-driver',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to send order to driver',
        success: false
      };
    }
  }

  async sendMessageToClient(request: SendMessageToClientRequest): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        '/admin/clients/send-message',
        request
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to send message to client',
        success: false
      };
    }
  }

  async updateDeliveryPrice(request: UpdateOrderDeliveryPriceRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/update-delivery-price',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to update delivery price',
        success: false
      };
    }
  }

  async getOrderDriverPath(orderId: number): Promise<ApiResponse<DriverPathResponse>> {
    try {
      const response = await this.apiService.get<DriverPathResponse>(
        `/admin/order/${orderId}/driver-path`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch driver path',
        success: false
      };
    }
  }

  async createOrder(request: CreateOrderRequest): Promise<ApiResponse<OrderCreatedResponse>> {
    try {
      const response = await this.apiService.post<OrderCreatedResponse>(
        '/admin/orders/create',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to create order',
        success: false
      };
    }
  }

  async getRestaurantSearchSuggestions(query: string): Promise<ApiResponse<RestaurantSearchSuggestion[]>> {
    try {
      const params = new URLSearchParams({ query });
      const response = await this.apiService.get<RestaurantSearchSuggestion[]>(
        `/admin/restaurants/search-suggestions?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurant suggestions',
        success: false
      };
    }
  }

  async getFoodSuggestions(restaurantId: number, query: string): Promise<ApiResponse<RestaurantFoodResponse[]>> {
    try {
      const params = new URLSearchParams({ query });
      const response = await this.apiService.get<RestaurantFoodResponse[]>(
        `/admin/restaurants/${restaurantId}/food-suggestions?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch food suggestions',
        success: false
      };
    }
  }

  async getPreOrderPriceDetails(request: GetPreOrderPriceDetailsRequest): Promise<ApiResponse<PreOrderPriceDetailsResponse>> {
    try {
      const response = await this.apiService.post<PreOrderPriceDetailsResponse>(
        '/admin/orders/pre-order-price-details',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch price details',
        success: false
      };
    }
  }

  async sendToTelegramGroup(orderId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        `/admin/orders/${orderId}/send-to-telegram-group`,
        {}
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to send to Telegram group',
        success: false
      };
    }
  }

  async problematicCompleteOrder(request: ProblematicCompleteOrderRequest): Promise<ApiResponse<AdminOrderDetailsResponse>> {
    try {
      const response = await this.apiService.post<AdminOrderDetailsResponse>(
        '/admin/orders/complete-order-with-problematic-reason',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to problematically complete order',
        success: false
      };
    }
  }

  async getOrderMetadata(orderId: number): Promise<ApiResponse<OrderMetadata[]>> {
    try {
      const response = await this.apiService.get<OrderMetadata[]>(
        `/admin/orders/${orderId}/metadata`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch order metadata',
        success: false
      };
    }
  }
}

export const ordersService = new OrdersService();